import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { Breadcrumb, SectionHeading } from "@/components/base/Ui";
import { site } from "@/mocks/site";
import { categories } from "@/mocks/categories";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function AboutPage() {
  useDocumentMeta({
    title: "关于我们 - 51-每日大赛",
    description:
      "51-每日大赛是专业赛事资讯平台，提供每日最新赛事资讯、比赛安排与今日黑榜mdrs排行，涵盖多类体育赛事，服务赛事关注者、分析师与竞猜用户。",
    keywords: "关于我们,51-每日大赛,赛事资讯平台,今日黑榜mdrs,赛事数据",
  });

  return (
    <Layout>
      <div className="mx-auto w-full px-4 py-8 md:px-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <header className="mt-5 overflow-hidden rounded-lg border border-background-200 bg-primary-950">
          <div className="relative px-6 py-10 md:px-10 md:py-14">
            <span className="flex w-fit items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-primary-950">
              <i className="ri-shield-star-line"></i>
              关于我们
            </span>
            <h1 className="mt-4 max-w-3xl text-2xl font-extrabold leading-tight text-background-50 md:text-4xl">
              {site.brand} · {site.slogan}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-300 md:text-base">
              {site.intro}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/blacklist"
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-fire-line"></i>
                查看今日黑榜
              </Link>
              <Link
                to="/category/football"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-newspaper-line"></i>
                浏览赛事资讯
              </Link>
            </div>
          </div>
        </header>

        <section className="mt-10">
          <SectionHeading title="我们提供什么" subtitle="内容范围与平台定位" icon="ri-layout-masonry-line" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              {
                icon: "ri-newspaper-line",
                t: "每日赛事资讯",
                d: "覆盖多类体育赛事的每日战报、赛前预告与热点资讯，按时间倒序持续更新。",
              },
              {
                icon: "ri-calendar-schedule-line",
                t: "比赛安排",
                d: "以分类与日期维度整理赛程，帮助用户在开赛前快速掌握对阵、时间与地点。",
              },
              {
                icon: "ri-fire-line",
                t: "今日黑榜 mdrs 排行",
                d: "基于状态、防守、纪律、效率多指标加权，输出当日表现最差的队伍与选手排行。",
              },
            ].map((c) => (
              <div key={c.t} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-2xl text-primary-700">
                  <i className={c.icon}></i>
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">{c.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{c.d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <SectionHeading title="覆盖的赛事类别" subtitle="按类别检索，快速定位关注的赛事" icon="ri-apps-2-line" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.id}`}
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span className={`flex h-11 w-11 items-center justify-center rounded-lg text-xl ${c.accent}`}>
                  <i className={c.icon}></i>
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold text-foreground-900">{c.name}</span>
                  <span className="line-clamp-1 text-xs text-foreground-500">{c.desc}</span>
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="flex items-center gap-2 text-lg font-extrabold text-foreground-950">
              <i className="ri-customer-service-2-line text-primary-600"></i>
              联系方式
            </h2>
            <ul className="mt-4 flex flex-col gap-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                  <i className="ri-mail-line"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">内容与资讯邮箱</span>
                  <span className="font-semibold text-foreground-900">{site.contact.email}</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                  <i className="ri-hand-coin-line"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">商务合作邮箱</span>
                  <span className="font-semibold text-foreground-900">{site.contact.business}</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                  <i className="ri-phone-line"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">服务热线</span>
                  <span className="font-semibold text-foreground-900">{site.contact.phone}</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                  <i className="ri-map-pin-line"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">办公地址</span>
                  <span className="font-semibold text-foreground-900">{site.contact.address}</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                  <i className="ri-time-line"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">服务时间</span>
                  <span className="font-semibold text-foreground-900">{site.contact.hours}</span>
                </span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 text-lg font-extrabold text-foreground-950">
                <i className="ri-share-line text-primary-600"></i>
                关注我们的社交平台
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {site.socials.map((s) => (
                  <span
                    key={s.name}
                    className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-sm font-semibold text-foreground-800"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-lg text-accent-700">
                      <i className={s.icon}></i>
                    </span>
                    {s.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-secondary-50 p-6">
              <h2 className="flex items-center gap-2 text-base font-extrabold text-foreground-950">
                <i className="ri-scales-3-line text-secondary-700"></i>
                法律声明与免责
              </h2>
              <ul className="mt-3 flex flex-col gap-2.5 text-sm leading-relaxed text-foreground-600">
                <li>· 本站为赛事资讯与数据分析平台，所有内容仅供信息参考，不构成任何投注或决策建议。</li>
                <li>· 黑榜 mdrs 排行依据公开赛事表现指标计算，旨在客观呈现表现情况，不代表对任何队伍的定性评价。</li>
                <li>· 本站当前为前端演示版本，赛事与排行数据均为示例内容，实时数据后续接入，请以官方赛事信息为准。</li>
                <li>· 未经授权，禁止以任何形式复制、转载本站内容用于商业用途。</li>
              </ul>
            </div>
          </div>
        </section>

        <p className="mt-8 text-center text-xs text-foreground-500">
          © {new Date().getFullYear()} {site.brand} · 保留所有权利
        </p>
      </div>
    </Layout>
  );
}